import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Loader2, Sparkles, Fingerprint } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Modulo94IdentidadOperativa() {
    const [autodefinicion, setAutodefinicion] = useState('');
    const [comportamientoReal, setComportamientoReal] = useState('');
    const [respuestaDificultad, setRespuestaDificultad] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');
    const [nivelAlineacion, setNivelAlineacion] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/identidad_operativa`, { withCredentials: true });
                if (data.data) {
                    if (data.data.autodefinicion) setAutodefinicion(data.data.autodefinicion);
                    if (data.data.comportamientoReal) setComportamientoReal(data.data.comportamientoReal);
                    if (data.data.respuestaDificultad) setRespuestaDificultad(data.data.respuestaDificultad);
                }
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch {} finally { setLoadingDraft(false); }
        })();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try { await axios.post(`${API_URL}/api/user/draft/save`, { module_id: 'identidad_operativa', data: { autodefinicion, comportamientoReal, respuestaDificultad } }, { withCredentials: true }); } catch {} finally { setSavingDraft(false); }
    }, [autodefinicion, comportamientoReal, respuestaDificultad]);

    useEffect(() => { if (loadingDraft || readOnly) return; const t = setTimeout(saveDraft, 2000); return () => clearTimeout(t); }, [autodefinicion, comportamientoReal, respuestaDificultad, saveDraft, loadingDraft, readOnly]);

    const canSubmit = !readOnly && (autodefinicion.trim().length > 0 && comportamientoReal.trim().length > 0 && respuestaDificultad.trim().length > 0);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/user/modules/identidad-operativa/submit`, {
                autodefinicion, comportamiento_real: comportamientoReal, respuesta_dificultad: respuestaDificultad
            }, { withCredentials: true });
            setAiInsight(data.ai_insight);
            setNivelAlineacion(data.nivel_alineacion);
            setSubmitted(true);
            toast.success('Identidad operativa completada');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); }
        finally { setLoading(false); }
    };

    if (loadingDraft) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <div className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl"><CardContent className="pt-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#7A9BA8] to-[#5F8494] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"><Fingerprint className="h-10 w-10 text-white" /></div>
                    <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2" data-testid="identidad-operativa-success-title">Identidad consolidada</h2>
                    <p className="text-[#7A9BA8] font-medium mb-4">Tu identidad se basa en lo que haces, no en lo que deseas ser</p>
                    {nivelAlineacion && <div className="inline-block px-4 py-1.5 bg-[#5FAF9D]/10 text-[#5FAF9D] rounded-full text-sm font-medium mb-4">Alineación: {nivelAlineacion}</div>}
                    {aiInsight && <div className="bg-[#F5F7F8] border-l-4 border-[#7A9BA8] p-4 rounded-r-xl text-left mb-6"><div className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-[#7A9BA8] shrink-0 mt-0.5" /><p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p></div></div>}
                    <div className="mb-6"><ModuleFeedback moduleId="identidad_operativa" chapter={9} /></div>
                    <Link to="/usuario"><Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl" data-testid="continue-after-identidad-operativa">Continuar</Button></Link>
                </CardContent></Card>
            </div><SENDAFooter /></div>
    );

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <header className="bg-white/80 backdrop-blur-xl border-b border-[#EAE8E3]/50 sticky top-0 z-50">
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Link to="/usuario" className="flex items-center gap-2 text-[#3E5F6E] hover:text-[#2F4A57]"><ArrowLeft className="h-5 w-5" /><span className="text-sm font-medium">Volver</span></Link>
                    <span className="text-xs text-[#9AA5B1]">{savingDraft ? 'Guardando...' : 'Borrador guardado'}</span>
                </div>
            </header>
            <main className="flex-1 max-w-3xl mx-auto px-4 py-8 w-full">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2"><div className="w-10 h-10 bg-[#3E5F6E] rounded-xl flex items-center justify-center"><Fingerprint className="h-5 w-5 text-white" /></div><span className="text-xs font-semibold text-[#9AA5B1] uppercase tracking-wider">Capítulo 9 · Módulo 9.4</span></div>
                    <h1 className="text-2xl sm:text-3xl font-light text-[#1F2A30] font-['Outfit'] mb-2">Identidad operativa</h1>
                    <p className="text-[#616E7C]">Consolida una identidad basada en comportamiento real, no en aspiraciones. Tu identidad es lo que haces.</p>
                </div>
                <div className="space-y-6">
                    <div>
                        <Label className="text-sm font-semibold text-[#1F2A30] mb-2 block">Autodefinición</Label>
                        <p className="text-xs text-[#9AA5B1] mb-2">¿Cómo te defines ahora, basándote en lo que realmente haces?</p>
                        <Textarea value={autodefinicion} onChange={e => setAutodefinicion(e.target.value)} placeholder="Yo soy alguien que..." className="min-h-[100px] border-[#DCE3E8] focus:border-[#3E5F6E]" data-testid="identidad-autodefinicion" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-[#1F2A30] mb-2 block">Comportamiento real</Label>
                        <p className="text-xs text-[#9AA5B1] mb-2">¿Qué comportamientos concretos respaldan esa definición?</p>
                        <Textarea value={comportamientoReal} onChange={e => setComportamientoReal(e.target.value)} placeholder="Lo que hago consistentemente es..." className="min-h-[100px] border-[#DCE3E8] focus:border-[#3E5F6E]" data-testid="identidad-comportamiento" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-[#1F2A30] mb-2 block">Respuesta ante la dificultad</Label>
                        <p className="text-xs text-[#9AA5B1] mb-2">¿Cómo respondes ahora cuando las cosas se ponen difíciles?</p>
                        <Textarea value={respuestaDificultad} onChange={e => setRespuestaDificultad(e.target.value)} placeholder="Cuando algo se pone difícil, yo..." className="min-h-[100px] border-[#DCE3E8] focus:border-[#3E5F6E]" data-testid="identidad-respuesta-dificultad" />
                    </div>
                    <Button onClick={handleSubmit} disabled={!canSubmit || loading} className="w-full bg-[#3E5F6E] hover:bg-[#2F4A57] text-white py-3 rounded-xl" data-testid="submit-identidad-operativa">
                        {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Analizando...</> : 'Completar identidad operativa'}
                    </Button>
                </div>
            </main>
            <SENDAFooter />
        </div>
    );
}
